import { type ChangeEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';

/** Tallest the box grows before it scrolls instead. */
const MAX_HEIGHT_PX = 160;

/** Fit the box to its content, up to MAX_HEIGHT_PX. Shared by typing and by a
 *  prefilled mount, so a draft opens at the height its text needs. */
function fit(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, MAX_HEIGHT_PX) + 'px';
}

export function Composer({
  onSend,
  onStop,
  theme,
  placeholder = 'Ask Helium AI about your network…',
  compact,
  disabled,
  autoFocus,
  initialValue,
}: {
  onSend: (value: string) => void;
  /** While a turn is streaming, the send button becomes a Stop button that
      calls this (cancels the in-flight turn). Omit to keep the plain composer. */
  onStop?: () => void;
  theme?: 'light' | 'dark';
  placeholder?: string;
  compact?: boolean;
  /** While a turn is streaming: keep the typed text but block sending, so a
      message isn't silently dropped (and never auto-sent on the next turn). */
  disabled?: boolean;
  /** Take focus on mount, so typing lands here without a click first. */
  autoFocus?: boolean;
  /** Text to open with, editable and NOT auto-sent, so the user reviews it and
      presses send themselves. Read once at mount (every entry point remounts
      the composer), which is also what keeps typing from being clobbered. */
  initialValue?: string;
}) {
  const [val, setVal] = useState(initialValue ?? '');
  const [focused, setFocused] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const dark = theme === 'dark';
  const canSend = Boolean(val.trim()) && !disabled;

  // Mount-only focus, so the first keystroke isn't dropped on the page. Every
  // entry point (fresh chat, New chat, reopened thread) remounts the composer —
  // App keys ChatView by sessionId/newNonce — so this can't steal focus back
  // mid-conversation. Skipped on touch-primary devices, where it would pop the
  // on-screen keyboard unasked. `preventScroll` because the hero's composer sits
  // mid-page in a scrollable column: letting the browser scroll it into view
  // would push the greeting and prompt chips off the top on a short viewport.
  useEffect(() => {
    if (!autoFocus) return;
    if (!window.matchMedia?.('(pointer: fine)').matches) return;
    taRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  // A prefilled draft never passes through `grow`, so size it here or a
  // multi-line draft opens collapsed to one row with its tail scrolled away.
  useEffect(() => {
    const el = taRef.current;
    if (el && initialValue) fit(el);
  }, [initialValue]);

  function submit() {
    // Preserve the typed text if we can't send yet (a turn is in flight) —
    // the user sends it themselves once the answer finishes.
    if (!canSend) return;
    onSend(val.trim());
    setVal('');
    if (taRef.current) taRef.current.style.height = 'auto';
  }
  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }
  function grow(e: ChangeEvent<HTMLTextAreaElement>) {
    setVal(e.target.value);
    fit(e.target);
  }

  // The composer must stay legible on two surfaces the theme tokens can't cover
  // at once: the dark hero panel (always dark, even in light theme → hardcoded
  // white-alpha) and the dark-theme floating composer. Both were near-invisible
  // ("black on black"); brighten the fill + border and add a focus ring so the
  // input clearly reads as an input.
  const idleBorder = dark ? 'rgba(255,255,255,.26)' : 'var(--border-1)';
  // While a turn streams the button stops it, rather than sitting disabled — so
  // a user is never stranded watching a runaway turn spin.
  const showStop = Boolean(disabled && onStop);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 10,
        background: dark ? 'rgba(255,255,255,.09)' : 'var(--bg-1)',
        border: `1px solid ${focused ? 'var(--he-purple)' : idleBorder}`,
        borderRadius: 16,
        padding: '8px 8px 8px 18px',
        boxShadow: focused
          ? '0 0 0 3px rgba(95,38,252,.22)'
          : compact
            ? 'var(--shadow-md)'
            : 'none',
        transition: 'border-color .15s, box-shadow .15s',
      }}
    >
      <textarea
        ref={taRef}
        rows={1}
        value={val}
        onChange={grow}
        onKeyDown={onKey}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        style={{
          flex: 1,
          border: 'none',
          background: 'transparent',
          resize: 'none',
          outline: 'none',
          fontFamily: 'inherit',
          fontSize: 15.5,
          lineHeight: 1.5,
          padding: '9px 0',
          color: dark ? '#fff' : 'var(--fg-1)',
          maxHeight: 160,
        }}
      />
      {showStop ? (
        <button
          onClick={onStop}
          className="he-press"
          aria-label="Stop"
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            flexShrink: 0,
            background: 'var(--he-purple)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background .15s',
          }}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
            <rect x="6" y="6" width="12" height="12" rx="2.5" fill="#fff" />
          </svg>
        </button>
      ) : (
        <button
          onClick={submit}
          disabled={!canSend}
          className="he-press"
          aria-label="Send"
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            flexShrink: 0,
            background: canSend ? 'var(--he-purple)' : dark ? 'rgba(255,255,255,.14)' : 'var(--bg-3)',
            border: 'none',
            cursor: canSend ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background .15s',
          }}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
            <path d="M4 12l16-8-6 16-3-7-7-1z" fill={canSend ? '#fff' : dark ? 'rgba(255,255,255,.6)' : 'var(--fg-3)'} />
          </svg>
        </button>
      )}
    </div>
  );
}
